import * as React from 'react'
import { animate, useReducedMotion } from 'framer-motion'
import type { Easing } from 'framer-motion'

/** Rounds to the nearest integer and formats with locale separators. */
export function formatLocaleInteger(value: number) {
  const rounded = Math.round(value)
  const safe = Number.isFinite(rounded) ? rounded : 0
  return Math.max(0, safe).toLocaleString()
}

interface UseAnimatedNumberOptions {
  value: number
  /** Animation duration in milliseconds. Default: 1000. */
  duration?: number
  ease?: Easing | Easing[]
  /** When provided, the hook returns a formatted string. */
  format?: (value: number) => string
  /** Snap to value on first render instead of animating from 0. Default: true. */
  snapOnMount?: boolean
}

/**
 * Tweens a numeric value from its previous state to the given target using
 * framer-motion's `animate()`.
 *
 * Mount behaviour is controlled by `snapOnMount` (default `true`):
 * - `true`  — snaps to value on mount, then animates between subsequent changes.
 *   Useful for live counters (balances, totals).
 * - `false` — animates from `0` up to value on mount. Useful for entrance
 *   animations on continuous visuals like SVG offsets and bar widths.
 *
 * When `format` is provided, the hook returns a formatted `string`.
 * When omitted, it returns a raw `number`.
 *
 * Respects `prefers-reduced-motion`: snaps immediately when enabled.
 */
export function useAnimatedNumber(
  options: UseAnimatedNumberOptions & { format: (value: number) => string },
): string
export function useAnimatedNumber(options: Omit<UseAnimatedNumberOptions, 'format'>): number
export function useAnimatedNumber(options: UseAnimatedNumberOptions): number | string {
  const {
    value: targetValue,
    duration = 1000,
    ease = 'easeOut',
    format: formatter,
    snapOnMount = true,
  } = options

  const prefersReducedMotion = useReducedMotion()
  const fromRef = React.useRef(snapOnMount ? targetValue : 0)
  const isFirstRenderRef = React.useRef(true)
  const [rawValue, setRawValue] = React.useState(() => {
    if (prefersReducedMotion) {
      return targetValue
    }
    return snapOnMount ? targetValue : 0
  })

  React.useEffect(() => {
    if (prefersReducedMotion) {
      fromRef.current = targetValue
      setRawValue(targetValue)
      isFirstRenderRef.current = false
      return
    }

    if (snapOnMount && isFirstRenderRef.current) {
      isFirstRenderRef.current = false
      fromRef.current = targetValue
      setRawValue(targetValue)
      return
    }

    if (duration <= 0) {
      fromRef.current = targetValue
      setRawValue(targetValue)
      isFirstRenderRef.current = false
      return
    }

    isFirstRenderRef.current = false

    const controls = animate(fromRef.current, targetValue, {
      duration: duration / 1000,
      ease,
      onUpdate(latest) {
        fromRef.current = latest
        setRawValue(latest)
      },
    })

    return () => {
      controls.stop()
    }
  }, [targetValue, duration, ease, prefersReducedMotion, snapOnMount])

  if (formatter) {
    return formatter(rawValue)
  }
  return rawValue
}
